/* eslint-disable no-nested-ternary */
import { Box, InputAdornment } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ClientCafe } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormDatePicker from '../../components/forms/FormDatePicker';
import FormPriceField from '../../components/forms/FormPriceField';
import FormRatio from '../../components/forms/FormRatio';
import FormTelField from '../../components/forms/FormTelField';
import FormTextField from '../../components/forms/FormTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { db } from '../../firebase';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';

type Props = {
  client?: DbDocument<ClientCafe> | null;
  onSubmit: () => Promise<void>;
  onClose: () => void;
};

const EditClient = ({ client, onClose, onSubmit }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { worker } = useCafe();
  const { lang } = useLocale();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<ClientCafe>({
    mode: 'onChange',
    defaultValues: {
      added: client?.added || Date.now(),
      type: 'individual',
      name: client?.name || '',
      phoneNumber: client?.phoneNumber || '',
      email: client?.email || '',
      address: client?.address || '',
      birthday: client?.birthday || 0,
      discountType: client?.discountType || 'auto',
      personalDiscount: client?.personalDiscount || 0,
      totalSum: client?.totalSum || 0,
    },
  });

  const { setError } = form;
  const discountType = form.watch('discountType');

  const saveChanges = useCallback(
    async (values: ClientCafe) => {
      setIsLoading(true);
      if (client?.id) {
        await updateDoc<ClientCafe>(`cafes/${cafeSlug}/clients/${client.id}`, values);
      } else {
        const docRef = doc(db, 'cafes', cafeSlug, 'clients', values.phoneNumber);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setError('phoneNumber', { message: t.client_with_phone_number_exists[lang] });
          setIsLoading(false);
          return;
        }
        await setDoc<ClientCafe>(`cafes/${cafeSlug}/clients/${values.phoneNumber}`, values);
      }

      await onSubmit();
      setIsLoading(false);
      onClose();
    },
    [cafeSlug, client?.id, lang, onClose, onSubmit, setError]
  );

  const remove = useCallback(async () => {
    setIsLoading(true);
    if (client?.id) {
      await deleteDoc(`cafes/${cafeSlug}/clients/${client.id}`);
    }
    await onSubmit();
    setIsLoading(false);
    onClose();
  }, [cafeSlug, client?.id, onClose, onSubmit]);

  return (
    <EditModal<ClientCafe>
      title={t.client[lang]}
      open
      onClose={onClose}
      secondaryButtonProps={{ disabled: isLoading }}
      submitButton={client ? t.save_changes[lang] : t.add_client[lang]}
      onSubmit={saveChanges}
      secondaryButton={worker?.credentials.set_personal_discount ? t.remove[lang] : undefined}
      confirmText={t.confirm_delete_client_or_group[lang]}
      onSecondaryButtonClick={remove}
      form={form}
      size="xs"
    >
      <Box sx={{ py: 2, mb: 1 }}>
        <FormTextField required sx={{ mb: 3 }} name="name" label={t.userName[lang]} fullWidth />

        <FormTelField
          autoComplete="off"
          required
          sx={{ mb: 3 }}
          name="phoneNumber"
          label={t.phone[lang]}
          disabled={!!client}
          fullWidth
        />

        <FormTextField name="email" label="Email" fullWidth sx={{ mb: 3 }} />

        <FormTextField name="address" label={t.address[lang]} fullWidth sx={{ mb: 3 }} />

        <FormDatePicker name="birthday" label={t.date_of_birth[lang]} sx={{ mb: 3 }} />

        <FormRatio
          name="discountType"
          label={t.discount_type[lang]}
          options={[
            { value: 'auto', label: t.determined_automatically[lang] },
            { value: 'personal', label: t.individual_discount[lang] },
          ]}
          disabled={!worker?.credentials.set_personal_discount}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 3 }}>
          <FormTextField
            name="personalDiscount"
            label={t.discount[lang]}
            disabled={!worker?.credentials.set_personal_discount || discountType === 'auto'}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (typeof val === 'number' && val >= 0 && val <= 100) {
                form.setValue('personalDiscount', val, { shouldDirty: true });
              }
            }}
          />

          <FormPriceField
            name="totalSum"
            label={t.spent_amount[lang]}
            disabled={!worker?.credentials.set_personal_discount}
            InputProps={{
              endAdornment: <InputAdornment position="end">{cafe?.currency}</InputAdornment>,
            }}
          />
        </Box>
      </Box>
    </EditModal>
  );
};

export default EditClient;
